import { FilterActions, type FilterV2ActionHandler, getFilterVariantState, type IFilterItem } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { EMPTY_APPLIED_FILTER_CHIPS } from "./constants";
import { FILTER_SECTION_V2_ORDER, FilterSectionV2Id } from "./FilterSectionV2Id";
import { FiltersV2RegistryContext, FiltersV2StateContext } from "./FiltersV2Context";
import getAppliedFilterChips from "./getAppliedFilterChips";
import type { AppliedFilterChipData, FilterSectionV2Registration } from "./types";

type FiltersV2ProviderProps = {
  isEnabled: boolean;
  children: React.ReactNode;
};

const getChipSignature = (chips: AppliedFilterChipData[]) => chips.map(({ key, isExcluded, label }) => `${key}|${isExcluded}|${label}`).join("|");

const getSectionItems = (refSectionItems: Map<FilterSectionV2Id, IFilterItem[]>) => {
  const sectionItems: Partial<Record<FilterSectionV2Id, IFilterItem[]>> = {};
  refSectionItems.forEach((items, sectionId) => {
    sectionItems[sectionId] = items;
  });
  return sectionItems;
};

const getAppliedItemCount = (items?: IFilterItem[]) =>
  items?.filter((filterOption) => getFilterVariantState(filterOption.variant).isSelected).length ?? 0;

const FiltersV2Provider = ({ isEnabled, children }: FiltersV2ProviderProps) => {
  const refSectionItems = useRef(new Map<FilterSectionV2Id, IFilterItem[]>());
  const refSectionHandlers = useRef(new Map<FilterSectionV2Id, FilterV2ActionHandler>());
  const refSectionActiveRefinementCounts = useRef(new Map<FilterSectionV2Id, number>());
  const refPendingRemovalKeys = useRef(new Set<string>());
  const [stateChips, setStateChips] = useState<AppliedFilterChipData[]>(EMPTY_APPLIED_FILTER_CHIPS);
  const [stateAppliedFilterCount, setStateAppliedFilterCount] = useState(0);

  const updateState = useCallback(() => {
    const allChips = getAppliedFilterChips({ sectionItems: getSectionItems(refSectionItems.current) });
    const allChipKeys = new Set<string>(allChips.map(({ key }) => key));
    refPendingRemovalKeys.current.forEach((key) => {
      if (!allChipKeys.has(key)) {
        refPendingRemovalKeys.current.delete(key);
      }
    });
    const nextChips = allChips.filter(({ key }) => !refPendingRemovalKeys.current.has(key));
    const nextSignature = getChipSignature(nextChips);
    const appliedFilterCount =
      nextChips.length +
      Array.from(refSectionActiveRefinementCounts.current.entries()).reduce(
        (totalCount, [sectionId, activeRefinementCount]) =>
          totalCount + Math.max(0, activeRefinementCount - getAppliedItemCount(refSectionItems.current.get(sectionId))),
        0,
      );
    setStateChips((currentChips) => (getChipSignature(currentChips) === nextSignature ? currentChips : nextChips));
    setStateAppliedFilterCount((currentAppliedFilterCount) => {
      return currentAppliedFilterCount === appliedFilterCount ? currentAppliedFilterCount : appliedFilterCount;
    });
  }, []);

  const registerSection = useCallback(
    ({ sectionId, items, actionHandler, activeRefinementCount = 0 }: FilterSectionV2Registration) => {
      refSectionItems.current.set(sectionId, items);
      refSectionHandlers.current.set(sectionId, actionHandler);
      refSectionActiveRefinementCounts.current.set(sectionId, activeRefinementCount);
      updateState();
    },
    [updateState],
  );

  const unregisterSection = useCallback(
    (sectionId: FilterSectionV2Id) => {
      refSectionItems.current.delete(sectionId);
      refSectionHandlers.current.delete(sectionId);
      refSectionActiveRefinementCounts.current.delete(sectionId);
      updateState();
    },
    [updateState],
  );

  const removeChip = useCallback(
    (chip: AppliedFilterChipData) => {
      if (refPendingRemovalKeys.current.has(chip.key)) {
        return;
      }
      const actionHandler = refSectionHandlers.current.get(chip.sectionId);
      if (!actionHandler) {
        return;
      }
      refPendingRemovalKeys.current.add(chip.key);
      actionHandler(chip.isExcluded ? FilterActions.EXCLUDE : FilterActions.INCLUDE, chip.value);
      updateState();
    },
    [updateState],
  );

  const clearAll = useCallback(() => {
    for (const sectionId of FILTER_SECTION_V2_ORDER) {
      const items = refSectionItems.current.get(sectionId);
      const activeRefinementCount = refSectionActiveRefinementCounts.current.get(sectionId) ?? 0;
      if (getAppliedItemCount(items) > 0 || activeRefinementCount > 0) {
        refSectionHandlers.current.get(sectionId)?.(FilterActions.RESET);
      }
    }
  }, []);

  const registryValue = useMemo(() => (isEnabled ? { registerSection, unregisterSection } : null), [isEnabled, registerSection, unregisterSection]);
  const stateValue = useMemo(
    () => ({
      chips: isEnabled ? stateChips : EMPTY_APPLIED_FILTER_CHIPS,
      appliedFilterCount: isEnabled ? stateAppliedFilterCount : 0,
      hasAppliedFilters: isEnabled && stateAppliedFilterCount > 0,
      removeChip,
      clearAll,
    }),
    [isEnabled, stateChips, stateAppliedFilterCount, removeChip, clearAll],
  );

  return (
    <FiltersV2RegistryContext.Provider value={registryValue}>
      <FiltersV2StateContext.Provider value={stateValue}>{children}</FiltersV2StateContext.Provider>
    </FiltersV2RegistryContext.Provider>
  );
};

export default FiltersV2Provider;
